import { Button, Dialog, DialogButtons, Modal, PencilSimpleIcon } from '@st/theme'
import type { ReactNode } from 'react'
import { match } from '@st/pattern'
import { type TaxReturnDeliveryDocumentState, selTaxReturnDeliveryPolicy } from './state'
import { useProcess, useSelect } from '~/store'
import { navigateToChecklist } from '../../st-folder-focus'
import { useEmailsManagedBy } from '~/features/app-hooks'

function TaxReturnDeliveryBanner({ colors, children }: { colors: string; children: ReactNode }) {
  return (
    <div
      className={`pointer-events-auto flex items-center justify-between gap-x-6 px-5 text-base ${colors}`}
    >
      {children}
    </div>
  )
}

type TaxReturnDeliveryTopBarProps = {
  state: TaxReturnDeliveryDocumentState | undefined
}

/**
 * The top bar is rendered for every delivery view, unlike the sidebar, which
 * the user can collapse. The leave dialog lives here so a navigation blocked
 * by unsaved sidebar edits always has a visible way to continue.
 */
export function TaxReturnDeliveryTopBar(props: TaxReturnDeliveryTopBarProps) {
  return (
    <>
      <TaxReturnDeliveryStatusBanner {...props} />
      <TaxReturnDeliveryLeaveDialog />
    </>
  )
}

function TaxReturnDeliveryLeaveDialog() {
  const folder = useProcess('stFolder')
  const delivery = useSelect('stFolder', (state) => {
    if (state.status != 'ready') throw new Error('Delivery editor requires a ready folder')
    return state.taxReturnDelivery
  })
  const keepEditing = () =>
    folder.send({ type: 'taxReturnDelivery', message: { type: 'leaveKept' } })

  return (
    <Modal isOpen={delivery.phase == 'confirmingLeave'} onClose={keepEditing}>
      <Dialog
        title={`Discard unsaved ${delivery.editor.type} changes?`}
        subtitle="Keep editing to save your changes, or discard them to continue."
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={keepEditing}>
              Keep editing
            </Button>
            <Button
              onClick={() =>
                folder.send({ type: 'taxReturnDelivery', message: { type: 'leaveDiscarded' } })
              }
            >
              Discard changes
            </Button>
          </DialogButtons>
        }
      />
    </Modal>
  )
}

function TaxReturnDeliveryStatusBanner({ state }: TaxReturnDeliveryTopBarProps) {
  const folder = useProcess('stFolder')
  const emailsManagedBy = useEmailsManagedBy()
  const { canDeliver, canCreateDraft, signingReady, canEdit, hasSummary } = useSelect(
    'stFolder',
    selTaxReturnDeliveryPolicy
  )
  const editButtons = (
    <DeliveryEditButtons
      onChecklistClick={() =>
        folder.send({ type: 'navigationRequested', request: navigateToChecklist('delivery') })
      }
      onPaymentLinkClick={() => folder.send({ type: 'paymentLinkOpened' })}
    />
  )
  const deliver = (contract: { id?: string }) =>
    folder.send({
      type: 'deliveryRequested',
      contractId: persistedContractId(contract),
      emailsManagedBy
    })
  return match(state)
    .with(undefined, () => (
      <TaxReturnDeliveryBanner colors="bg-yellow-300 text-yellow-900">
        <div>This tax return delivery has no draft yet.</div>
        <Button
          variant="primary"
          disabled={!canCreateDraft}
          onClick={() =>
            folder.send({ type: 'taxReturnDelivery', message: { type: 'draftCreateRequested' } })
          }
        >
          Create draft
        </Button>
      </TaxReturnDeliveryBanner>
    ))
    .with({ contract: { status: 'submitted' } }, () => (
      <TaxReturnDeliveryBanner colors="bg-green-300 text-green-800">
        <div>This tax return delivery has been signed.</div>
        {editButtons}
      </TaxReturnDeliveryBanner>
    ))
    .with(
      {
        contract: {
          status: 'published',
          emailSent: true
        }
      },
      () => (
        <TaxReturnDeliveryBanner colors="bg-green-300 text-green-800">
          <div>This tax return delivery is waiting for client signature.</div>
          <div className="flex items-center gap-2">
            {editButtons}
            <Button variant="primary" disabled>
              Sent
            </Button>
          </div>
        </TaxReturnDeliveryBanner>
      )
    )
    .with({ contract: { status: 'draft' } }, ({ contract, hasOtherPublishedDelivery }) => (
      <TaxReturnDeliveryBanner colors="bg-yellow-300 text-yellow-900">
        <div>
          {match({ hasOtherPublishedDelivery, signingReady })
            .with(
              { hasOtherPublishedDelivery: true },
              () => 'Another tax return delivery is already published.'
            )
            .with(
              { signingReady: false },
              () => 'Name each signer and add their signature field before delivering.'
            )
            .with({ signingReady: true }, () => 'This tax return delivery is ready to send.')
            .exhaustive()}
        </div>
        <div className="flex items-center gap-2">
          {hasSummary && (
            <Button
              variant="white"
              disabled={!canEdit}
              onClick={() => folder.send({ type: 'taxReturnDeliverySummaryOpened' })}
            >
              Edit Summary Page
            </Button>
          )}
          <DeliverButton disabled={!canDeliver} onClick={() => deliver(contract)} />
        </div>
      </TaxReturnDeliveryBanner>
    ))
    .with({ contract: { status: 'published' } }, ({ contract }) => (
      <TaxReturnDeliveryBanner colors="bg-green-300 text-green-800">
        <div>This tax return delivery is waiting for client signature.</div>
        <div className="flex items-center gap-2">
          {editButtons}
          <DeliverButton disabled={!canDeliver} onClick={() => deliver(contract)} />
        </div>
      </TaxReturnDeliveryBanner>
    ))
    .run()
}

function DeliveryEditButtons({
  onChecklistClick,
  onPaymentLinkClick
}: {
  onChecklistClick: () => void
  onPaymentLinkClick: () => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="white"
        leadingIcon={<PencilSimpleIcon className="size-4" />}
        onClick={onChecklistClick}
      >
        Checklist
      </Button>
      <Button
        variant="white"
        leadingIcon={<PencilSimpleIcon className="size-4" />}
        onClick={onPaymentLinkClick}
      >
        Invoice link
      </Button>
    </div>
  )
}

function DeliverButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button variant="primary" disabled={disabled} onClick={onClick}>
      Deliver to client
    </Button>
  )
}

function persistedContractId(contract: { id?: string }): string {
  if (!contract.id) throw new Error('Cannot deliver a contract without an ID')
  return contract.id
}
